// Company stores (S3, 2026-10-12; S4 shop 2026-10-12; mock-ups + "From"
// 2026-10-19) — one product in a store: its picture, name, how it's branded
// and the colours people can pick. The picture is the rep's approved
// mock-up of the store's logo on it when there is one (labelled as a
// mock-up), else the plain photo. "From $X each" is the server's own price
// (GET /portal/store/prices — the same priceCart the product page asks),
// shown only when the server gave one: never a $0. In the store itself
// (`shop`) the card opens the product; the sign-off preview isn't a link.
import { useState } from "react";
import { Link } from "react-router-dom";
import { leadImage, type StoreProduct } from "@/lib/portal-store";
import { fromPriceWords, type StoreFromPrice } from "@/lib/store-shop";

export function StoreProductCard({ product, shop = false, price = null }: { product: StoreProduct; shop?: boolean; price?: StoreFromPrice | null }) {
  // A mock-up link that won't load (expired, removed) falls back to the plain photo.
  const [mockupBroken, setMockupBroken] = useState(false);
  const image = mockupBroken ? (product.photo ? { src: product.photo, mockup: false } : null) : leadImage(product);
  const from = shop ? fromPriceWords(price) : null;
  const body = (
    <>
      <div className="relative flex aspect-square items-center justify-center bg-canvas">
        {image ? (
          <img
            src={image.src}
            alt={image.mockup ? `${product.name} with your logo (mock-up)` : product.name}
            loading="lazy"
            className="h-full w-full object-contain p-4"
            onError={image.mockup ? () => setMockupBroken(true) : undefined}
          />
        ) : (
          <span className="text-xs text-muted">Photo coming soon</span>
        )}
        {image?.mockup && (
          <span className="absolute bottom-2 left-2 rounded-full bg-cream-100 px-2 py-0.5 text-[0.6875rem] font-medium text-brown-500">Mock-up with your logo</span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3 sm:p-4">
        <h3 className="text-sm font-semibold leading-snug text-ink">{product.name}</h3>
        {from && (
          <p className="text-sm text-ink">
            <span className="font-semibold">{from.lead}</span> <span className="text-xs text-brown-400">{from.detail}</span>
          </p>
        )}
        {product.branding && (
          <p className="text-xs text-brown-400">
            {shop ? "Branded" : "Logo"}: {shop ? product.branding.charAt(0).toLowerCase() + product.branding.slice(1) : product.branding}
          </p>
        )}
        {product.colours.length > 0 && (
          <p className="text-xs text-brown-400">
            {product.colours.length === 1 ? "Colour" : "Colours"}: {product.colours.join(", ")}
          </p>
        )}
      </div>
    </>
  );
  if (shop) {
    return (
      <Link
        to={`/store/product/${encodeURIComponent(product.productId)}`}
        className="flex flex-col overflow-hidden rounded-lg border border-rule bg-white transition-shadow hover:shadow-md"
      >
        {body}
      </Link>
    );
  }
  return <article className="flex flex-col overflow-hidden rounded-lg border border-rule bg-white">{body}</article>;
}

export function StoreProductGrid({
  products,
  shop = false,
  prices,
  wide = false,
}: {
  products: StoreProduct[];
  shop?: boolean;
  /** The grid's "From" prices by product id (shop only). */
  prices?: ReadonlyMap<string, StoreFromPrice>;
  /** In the full-width portal page: more columns on wide screens. Off for
   *  the 960px public sign-off page, where 4 across is already the most. */
  wide?: boolean;
}) {
  return (
    <div className={`grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4${wide ? " xl:grid-cols-5 2xl:grid-cols-6" : ""}`}>
      {products.map((p) => (
        <StoreProductCard key={p.id} product={p} shop={shop} price={prices?.get(p.productId) ?? null} />
      ))}
    </div>
  );
}
